import { Button, ButtonVariant, IconErrorCircle } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { type FC } from 'react'
import { usePlaidMultiLinkContext } from '../plaid-multi-link-context/plaid-multi-link-context'
import { PlaidErrorType } from '../../../types/plaid-multi-link'

/**
 * Reusable error component for Plaid Multi Link flow
 * Now reads error type and handlers from context
 */
export const PlaidError: FC = () => {
  const { t } = useTranslation('features-external-accounts')
  const {
    currentErrorType,
    handleTryAgain,
    closeModal,
    isCreatingPlaidMultiLinkToken,
    isUnlinkingPlaidExternalInstitution,
    isInitiatingRelinkPlaidExternalInstitution,
    isUpdatingPlaidExternalInstitution,
    isCompletingRelinkPlaidExternalAccount,
    isCompletingUpdatePlaidExternalAccount,
  } = usePlaidMultiLinkContext()

  // Get error-specific translations based on error type
  const getErrorContent = () => {
    switch (currentErrorType) {
      case PlaidErrorType.INITIAL_INSTITUTION_LINK_TOKEN_CREATION:
        return {
          header: t('plaid_multi_link.error.title.initial_institution_link_token_creation'),
          message: t('plaid_multi_link.error.message.initial_institution_link_token_creation'),
        }
      case PlaidErrorType.INITIAL_INSTITUTION_LINK_ERROR:
        return {
          header: t('plaid_multi_link.error.title.initial_institution_link_error'),
          message: t('plaid_multi_link.error.message.initial_institution_link_error'),
        }

      case PlaidErrorType.INSTITUTION_REMOVAL_ERROR:
        return {
          header: t('plaid_multi_link.error.title.institution_removal_error'),
          message: t('plaid_multi_link.error.message.institution_removal_error'),
        }

      case PlaidErrorType.RELINK_INSTITUTION_TOKEN_CREATION:
        return {
          header: t('plaid_multi_link.error.title.relink_institution_token_creation'),
          message: t('plaid_multi_link.error.message.relink_institution_token_creation'),
        }
      case PlaidErrorType.RELINK_INSTITUTION_ERROR:
        return {
          header: t('plaid_multi_link.error.title.relink_institution_error'),
          message: t('plaid_multi_link.error.message.relink_institution_error'),
        }
      case PlaidErrorType.UPDATING_INSTITUTION_TOKEN_CREATION:
        return {
          header: t('plaid_multi_link.error.title.updating_institution_token_creation'),
          message: t('plaid_multi_link.error.message.updating_institution_token_creation'),
        }
      case PlaidErrorType.UPDATING_INSTITUTION_ERROR:
        return {
          header: t('plaid_multi_link.error.title.updating_institution_error'),
          message: t('plaid_multi_link.error.message.updating_institution_error'),
        }
      default:
        return {
          header: t('plaid_multi_link.error.title.plaid_linking'),
          message: t('plaid_multi_link.error.message.plaid_linking'),
        }
    }
  }

  const errorContent = getErrorContent()

  const isLoading =
    isCreatingPlaidMultiLinkToken ||
    isUnlinkingPlaidExternalInstitution ||
    isInitiatingRelinkPlaidExternalInstitution ||
    isCompletingRelinkPlaidExternalAccount ||
    isUpdatingPlaidExternalInstitution ||
    isCompletingUpdatePlaidExternalAccount

  return currentErrorType ? (
    <div role="alert" aria-live="polite" aria-labelledby="error-title" data-testid="plaid-error">
      <IconErrorCircle className="mb-sm" />
      <p className="mb-sm typo-primary-2xl-light text-text-error">{errorContent.header}</p>
      <p className="mb-lg typo-primary-base-regular">{errorContent.message}</p>
      <Button
        variant={ButtonVariant.Primary}
        size="small"
        onClick={handleTryAgain}
        className="mb-2xs w-full"
        data-testid="plaid-error-retry-button"
        isDisabled={isLoading}
        isLoading={isLoading}
      >
        {t('plaid_multi_link.try_again')}
      </Button>
      <Button
        variant={ButtonVariant.Secondary}
        size="small"
        onClick={closeModal}
        className="mb-2xs w-full"
        data-testid="plaid-error-cancel-button"
      >
        {t('plaid_link.cancel')}
      </Button>
    </div>
  ) : null
}
